import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar as CalendarIcon, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

/**
 * SlotPicker — lets a vendor or couple reserve a 1-hour introduction slot.
 *
 * Props:
 *  - role: "vendor" | "couple" (controls the accent colour: blue | purple)
 *  - value: { date, time } | null
 *  - onChange: (nextValue | null) => void
 *  - label: optional question label
 */
const SlotPicker = ({ role = "vendor", value, onChange, testId }) => {
  const accent = role === "couple" ? "purple" : "blue";

  const accentClasses = useMemo(
    () =>
      accent === "purple"
        ? {
            selected:
              "bg-purple-500/15 border-purple-500 text-purple-900 hover:bg-purple-500/20",
            hover:
              "border-[var(--ld-ink)]/20 hover:border-purple-500 hover:text-purple-900",
            ring: "ring-purple-500",
            dot: "bg-purple-500",
            toast: "text-purple-800",
          }
        : {
            selected:
              "bg-sky-500/15 border-sky-500 text-sky-900 hover:bg-sky-500/20",
            hover:
              "border-[var(--ld-ink)]/20 hover:border-sky-500 hover:text-sky-900",
            ring: "ring-sky-500",
            dot: "bg-sky-500",
            toast: "text-sky-800",
          },
    [accent]
  );

  const [slots, setSlots] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [activeDate, setActiveDate] = useState(value?.date || "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/intro-slots`, {
          params: { role },
        });
        if (!cancelled) setSlots(r.data);
      } catch (e) {
        if (!cancelled) setError("Could not load slots. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role]);

  // Re-sync activeDate when a date is cleared
  useEffect(() => {
    if (value?.date) setActiveDate(value.date);
  }, [value?.date]);

  // Build date -> [time] map of available slots
  const availableByDate = useMemo(() => {
    if (!slots) return {};
    const map = {};
    slots.available.forEach((s) => {
      (map[s.date] = map[s.date] || []).push(s.time);
    });
    Object.keys(map).forEach((k) => map[k].sort());
    return map;
  }, [slots]);

  const availableDates = useMemo(() => {
    if (!slots) return [];
    return Object.keys(availableByDate).sort().slice(0, 21); // next ~3 weeks of weekdays
  }, [availableByDate, slots]);

  // If the current selected date is no longer available and nothing set, auto-pick the first
  useEffect(() => {
    if (slots && !activeDate && availableDates.length > 0) {
      setActiveDate(availableDates[0]);
    }
  }, [slots, activeDate, availableDates]);

  const handlePick = (date, time) => {
    onChange({ date, time });
  };

  const handleClear = () => {
    onChange(null);
  };

  if (error) {
    return (
      <p className="text-sm text-red-700" data-testid={`${testId}-error`}>
        {error}
      </p>
    );
  }
  if (!slots) {
    return (
      <p
        className="text-sm text-[var(--ld-ink-muted)]"
        data-testid={`${testId}-loading`}
      >
        Loading available slots…
      </p>
    );
  }
  if (availableDates.length === 0) {
    return (
      <div
        className="border border-dashed border-[var(--ld-gold-deep)]/40 bg-[var(--ld-gold)]/10 px-5 py-8 text-center"
        data-testid={`${testId}-empty`}
        role="alert"
      >
        <p className="text-[13px] tracking-[0.2em] uppercase text-[var(--ld-gold-deep)] font-accent">
          Please contact Lux.
        </p>
        <p className="mt-3 text-sm text-[var(--ld-ink)]">
          No introduction slots are open right now.
        </p>
        <p className="mt-1 text-[12px] text-[var(--ld-ink-muted)]">
          Reach out to the Luxé Directory team directly and we&apos;ll
          arrange a time that works for you.
        </p>
      </div>
    );
  }

  const daysByMonth = availableDates.reduce((acc, iso) => {
    const m = iso.slice(0, 7);
    (acc[m] = acc[m] || []).push(iso);
    return acc;
  }, {});

  return (
    <div data-testid={testId} className="space-y-5">
      {/* Summary pill of the selected slot */}
      {value?.date && value?.time ? (
        <div
          className={
            "flex items-center justify-between gap-3 px-4 py-3 border " +
            accentClasses.selected
          }
          data-testid={`${testId}-selected`}
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 size={16} strokeWidth={1.8} />
            <div className="text-[13px]">
              <div className="font-medium">
                {formatDateLabel(value.date)} · {value.time}
              </div>
              <div className="text-[11px] opacity-70">
                This slot will be reserved when you submit the form.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            data-testid={`${testId}-clear`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase opacity-80 hover:opacity-100"
          >
            <X size={12} strokeWidth={1.8} />
            Clear
          </button>
        </div>
      ) : (
        <p className="flex items-center gap-2 text-[12px] text-[var(--ld-ink-muted)]">
          <CalendarIcon size={13} strokeWidth={1.6} />
          Pick a date first, then choose a time.
        </p>
      )}

      {/* Date strip grouped by month */}
      <div className="space-y-4">
        {Object.entries(daysByMonth).map(([ym, dates]) => {
          const [year, month] = ym.split("-");
          return (
            <div key={ym}>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] mb-2">
                {MONTH_NAMES[parseInt(month, 10) - 1]} {year}
              </div>
              <div
                className="flex flex-wrap gap-2"
                data-testid={`${testId}-dates`}
              >
                {dates.map((iso) => {
                  const selected = iso === activeDate;
                  return (
                    <button
                      key={iso}
                      type="button"
                      data-testid={`${testId}-date-${iso}`}
                      onClick={() => setActiveDate(iso)}
                      className={
                        "min-w-[90px] px-3 py-2 text-[11px] tracking-[0.1em] uppercase border transition-colors " +
                        (selected
                          ? accentClasses.selected
                          : "bg-transparent text-[var(--ld-ink)] " +
                            accentClasses.hover)
                      }
                    >
                      {formatDateLabel(iso)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time slots for the active date */}
      {activeDate && (
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] mb-2">
            Times for {formatDateLabel(activeDate)}
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid={`${testId}-times`}
          >
            {(availableByDate[activeDate] || []).length === 0 && (
              <p className="text-sm text-[var(--ld-ink-muted)]">
                No times remaining on this day. Please pick another date.
              </p>
            )}
            {(availableByDate[activeDate] || []).map((t) => {
              const selected =
                value?.date === activeDate && value?.time === t;
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`${testId}-time-${t}`}
                  onClick={() => handlePick(activeDate, t)}
                  className={
                    "px-3 py-2 text-[12px] border transition-colors " +
                    (selected
                      ? accentClasses.selected
                      : "bg-transparent text-[var(--ld-ink)] " +
                        accentClasses.hover)
                  }
                >
                  {t}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-[var(--ld-ink-muted)] flex items-center gap-2">
            <span className={"h-2 w-2 rounded-full " + accentClasses.dot} />
            Slots already reserved by another applicant are hidden
            automatically.
          </p>
        </div>
      )}
    </div>
  );
};

export default SlotPicker;
